/**
 * Scoring Calculations for Golf Outings
 * Modular scoring utilities.
 */

// Standard golf ball diameter in inches (USGA regulation)
export const GOLF_BALL_DIAMETER_INCHES = 1.680;
export const GOLF_BALL_DIAMETER_MM = 42.67;

// Calculate team score based on format
export function calculateTeamScore(playerScores, format, holes) {
  if (!playerScores || playerScores.length === 0) return null;
  
  const holeCount = holes?.length || 18;
  const teamScores = [];
  
  for (let i = 0; i < holeCount; i++) {
    const holeScores = playerScores
      .map(p => p.hole_scores?.[i])
      .filter(s => s !== null && s !== undefined);
    
    if (holeScores.length === 0) {
      teamScores.push(null);
      continue;
    }
    
    switch (format) {
      case 'scramble':
      case 'best_ball':
        // Best score from the team
        teamScores.push(Math.min(...holeScores));
        break;
      case 'shamble':
        // After best drive, take best individual score
        teamScores.push(Math.min(...holeScores));
        break;
      case 'chapman':
      case 'alternate_shot':
        // Single score (partners alternate)
        teamScores.push(holeScores[0]);
        break;
      default:
        teamScores.push(Math.min(...holeScores));
    }
  }
  
  return teamScores;
}

// Calculate total score
export function calculateTotal(holeScores) {
  if (!holeScores || holeScores.length === 0) return null;
  const validScores = holeScores.filter(s => s !== null && s !== undefined);
  return validScores.length > 0 ? validScores.reduce((sum, s) => sum + s, 0) : null;
}

// Calculate front 9 total
export function calculateFront9(holeScores) {
  if (!holeScores || holeScores.length < 9) return null;
  const front = holeScores.slice(0, 9);
  return calculateTotal(front);
}

// Calculate back 9 total
export function calculateBack9(holeScores) {
  if (!holeScores || holeScores.length < 18) return null;
  const back = holeScores.slice(9, 18);
  return calculateTotal(back);
}

// Calculate relative to par
export function calculateRelativeToPar(score, pars) {
  if (!score || !pars) return null;
  const totalPar = Array.isArray(pars) ? pars.reduce((sum, p) => sum + (p || 4), 0) : pars;
  return score - totalPar;
}

// Determine if score is birdie, eagle, etc.
export function getScoreType(score, par) {
  if (!score || !par) return null;
  const diff = score - par;
  
  if (diff <= -3) return 'albatross';
  if (diff === -2) return 'eagle';
  if (diff === -1) return 'birdie';
  if (diff === 0) return 'par';
  if (diff === 1) return 'bogey';
  if (diff === 2) return 'double_bogey';
  if (diff >= 3) return 'triple_plus';
  
  return null;
}

// Check if score should trigger notification
export function shouldTriggerNotification(score, par) {
  const type = getScoreType(score, par);
  return ['albatross', 'eagle', 'birdie'].includes(type);
}

// Get notification type for score
export function getNotificationTypeForScore(score, par) {
  if (score === 1) return 'hole_in_one';
  
  const diff = score - par;
  if (diff <= -2) return 'eagle';
  if (diff === -1) return 'birdie';
  
  return null;
}

// Calculate handicap-adjusted score
export function calculateHandicapAdjustedScore(grossScore, courseHandicap, holesPlayed = 18) {
  if (typeof grossScore !== 'number' || !Number.isFinite(grossScore) || typeof courseHandicap !== 'number' || !Number.isFinite(courseHandicap)) return null;
  
  // Simple net score calculation
  const handicapStrokes = Math.round(courseHandicap * (holesPlayed / 18));
  return grossScore - handicapStrokes;
}

// Distribute handicap strokes across holes
export function distributeHandicapStrokes(courseHandicap, holeHandicaps) {
  if (typeof courseHandicap !== 'number' || !Number.isFinite(courseHandicap)) return Array(holeHandicaps?.length || 18).fill(0);
  // holeHandicaps is an array of handicap indices for each hole (1-18, lower = harder)
  if (!holeHandicaps || holeHandicaps.length === 0) {
    // Default distribution if no hole handicaps
    return Array(18).fill(Math.floor(courseHandicap / 18));
  }
  
  const strokes = Array(holeHandicaps.length).fill(0);
  let remainingStrokes = Math.round(courseHandicap);
  
  // Sort holes by handicap index and assign strokes
  const sortedIndices = holeHandicaps
    .map((hcp, idx) => ({ hcp, idx }))
    .sort((a, b) => a.hcp - b.hcp);
  
  for (const { idx } of sortedIndices) {
    if (remainingStrokes <= 0) break;
    strokes[idx]++;
    remainingStrokes--;
  }
  
  return strokes;
}

// Calculate skins winners
export function calculateSkinsWinners(teamScores, carryover = true) {
  // teamScores: Array of { teamId, teamName, holeScores: number[] }
  if (!teamScores || teamScores.length === 0) return [];
  
  const holeCount = teamScores[0].holeScores?.length || 18;
  const results = [];
  let carryoverSkins = 0;
  
  for (let hole = 0; hole < holeCount; hole++) {
    const scores = teamScores
      .map(t => ({
        teamId: t.teamId,
        teamName: t.teamName,
        score: t.holeScores?.[hole]
      }))
      .filter(s => s.score !== null && s.score !== undefined)
      .sort((a, b) => a.score - b.score);
    
    if (scores.length === 0) continue;
    
    const lowestScore = scores[0].score;
    const winners = scores.filter(s => s.score === lowestScore);
    
    if (winners.length === 1) {
      // Clear winner
      results.push({
        hole: hole + 1,
        winner: winners[0],
        skinsWon: 1 + carryoverSkins
      });
      carryoverSkins = 0;
    } else if (carryover) {
      // Tie - carryover
      carryoverSkins++;
      results.push({
        hole: hole + 1,
        winner: null,
        tied: winners,
        carriedOver: true
      });
    } else {
      // Tie - no carryover, split
      results.push({
        hole: hole + 1,
        winner: null,
        tied: winners,
        split: true
      });
    }
  }
  
  return results;
}

// Calculate CTP winner for a hole
export function calculateCTPWinner(entries) {
  if (!entries || entries.length === 0) return null;
  
  const sorted = [...entries].sort((a, b) => a.distance_inches - b.distance_inches);
  return sorted[0];
}

// Validate hole score
export function validateHoleScore(score, par) {
  if (score === null || score === undefined) return { valid: true, warning: null };
  
  if (score < 1) {
    return { valid: false, error: 'Score must be at least 1' };
  }
  
  if (score > 15) {
    return { valid: false, error: 'Score cannot exceed 15' };
  }
  
  // Warnings for unlikely scores
  if (score === 1 && par !== 3) {
    return { valid: true, warning: 'Hole in one on a non-par 3?' };
  }
  
  if (score > par + 4) {
    return { valid: true, warning: 'That\'s a high score - double check?' };
  }
  
  return { valid: true, warning: null };
}

// Build scorecard summary
export function buildScorecardSummary(holeScores, pars) {
  const total = calculateTotal(holeScores);
  const front9 = calculateFront9(holeScores);
  const back9 = calculateBack9(holeScores);
  const totalPar = pars ? calculateTotal(pars) : 72;
  const relativeToPar = total ? total - totalPar : null;
  
  // Count score types
  let birdies = 0, pars_count = 0, bogeys = 0, others = 0;
  
  holeScores.forEach((score, i) => {
    if (!score) return;
    const par = pars?.[i] || 4;
    const type = getScoreType(score, par);
    
    if (type === 'birdie' || type === 'eagle' || type === 'albatross') birdies++;
    else if (type === 'par') pars_count++;
    else if (type === 'bogey') bogeys++;
    else others++;
  });
  
  return {
    total,
    front9,
    back9,
    relativeToPar,
    relativeDisplay: relativeToPar === 0 ? 'E' : relativeToPar > 0 ? `+${relativeToPar}` : `${relativeToPar}`,
    holesPlayed: holeScores.filter(s => s !== null && s !== undefined).length,
    stats: { birdies, pars: pars_count, bogeys, others }
  };
}
